/**
 * Interactive Validators — Drag-drop, matching, ordering, and categorization question types.
 *
 * Extracted from validatorRegistry.ts during R1-S1 god-file split.
 * Covers: ordering, matching, drag_drop_fill, table_reading, schedule_reading, categorize.
 */

import type { ScheduleEvent } from '../../types';
import type { ValidatorFn } from './shared';

// ── Ordering ─────────────────────────────────────────────────

export const validateOrdering: ValidatorFn = (userAnswer, question) => {
  const userIndices = String(userAnswer).split(',').map(Number);
  const config = question.data || {};
  // Support both flat and nested data formats:
  // Flat: data.correctOrder   Nested: data.ordering.correctOrder
  const orderingData = config.ordering || config;
  const correctOrder = orderingData.correctOrder || [];
  const isCorrect = userIndices.length === correctOrder.length && userIndices.every((val: number, index: number) => val === correctOrder[index]);
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0
  };
};

// ── Matching ─────────────────────────────────────────────────

export const validateMatching: ValidatorFn = (userAnswer, question) => {
  let userPairs: [number, number][] = [];
  try {
    userPairs = JSON.parse(String(userAnswer));
  } catch {

    /* empty */}
  const config = question.data || {};
  // Support multiple data formats for expected pair count:
  // Format A: data.pairs   Format B: data.matching.pairs   Format C: data.leftItems
  const expectedLength = config.pairs?.length || config.matching?.pairs?.length || config.leftItems?.length || 0;
  const isCorrect = userPairs.every(([left, right]) => left === right) && userPairs.length === expectedLength;
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0
  };
};

// ── Fraction Equivalence Match ───────────────────────────────

export const validateFractionEquivalenceMatch: ValidatorFn = (userAnswer, question) => {
  let userPairs: [number, number][] = [];
  try {
    userPairs = JSON.parse(String(userAnswer));
  } catch {

    /* empty */}
  const config = question.data?.fractionEquivalenceMatch || question.data || {};
  const pairs = config.pairs || [];
  const expectedLength = pairs.length;
  const isCorrect = userPairs.every(([left, right]) => left === right) && userPairs.length === expectedLength;
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0
  };
};

// ── Drag & Drop Fill ─────────────────────────────────────────

export const validateDragDropFill: ValidatorFn = (userAnswer, question) => {
  let userAnswers: Record<string, string> = {};
  try {
    userAnswers = JSON.parse(String(userAnswer));
  } catch {

    /* empty */}
  const config = question.data || {};
  let blanks: Array<{
    id: string;
    correctAnswer: string;
  }> = config.blanks || [];

  // Format B: draggables + correctPlacements (no blanks array)
  if (blanks.length === 0 && config.draggables && config.correctPlacements) {
    const draggables = config.draggables as string[];
    const placements = config.correctPlacements as Record<string, number>;
    blanks = Object.entries(placements).map(([id, dragIndex]) => ({
      id,
      correctAnswer: draggables[dragIndex] || ''
    }));
  }
  const isCorrect = blanks.length > 0 && blanks.every(blank => userAnswers[blank.id] === blank.correctAnswer);
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0
  };
};

// ── Table Reading ────────────────────────────────────────────

export const validateTableReading: ValidatorFn = (userAnswer, question) => {
  const config = question.data || {};
  const isCorrect = Number(userAnswer) === config.correctSelection;
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0
  };
};

// ── Schedule Reading ─────────────────────────────────────────

export const validateScheduleReading: ValidatorFn = (userAnswer, question) => {
  const config = question.data || {};
  const correctCell = config.correctCell;
  const schedule = config.schedule || {
    columns: [],
    timeSlots: [],
    events: []
  };
  if (!correctCell) return {
    isCorrect: false,
    distractorIndex: 0
  };
  const [userRow, userCol] = String(userAnswer).split('-').map(Number);
  const isCorrect = userRow === correctCell.row && userCol === correctCell.col;
  const findEvent = (row: number, col: number) => schedule.events?.find((e: ScheduleEvent) => e.row === row && e.col === col);
  const userEvent = findEvent(userRow, userCol);
  const correctEvent = findEvent(correctCell.row, correctCell.col);
  const userLabel = userEvent?.content || `rij ${userRow + 1}, kolom ${userCol + 1}`;
  const correctLabel = correctEvent?.content || `rij ${correctCell.row + 1}, kolom ${correctCell.col + 1}`;
  const distractorKey = userRow * schedule.columns.length + userCol;
  return {
    isCorrect,
    distractorIndex: distractorKey,
    displayAnswer: isCorrect ? correctLabel : `${userLabel} (juiste antwoord: ${correctLabel})`,
    correctAnswerDisplay: correctLabel
  };
};

// ── Categorize ───────────────────────────────────────────────

export const validateCategorize: ValidatorFn = (userAnswer, question) => {
  let userAssignments: Record<string, number> = {};
  try {
    userAssignments = JSON.parse(String(userAnswer));
  } catch {

    /* empty */}
  const config = question.data || {};
  const items: Array<{
    text: string;
    correctCategory: number;
  }> = config.items || [];

  // Count correctly-placed items across ALL items (full pass — no early-out),
  // so the displayed "x/y correct" reflects every item even when an early one
  // is wrong. isCorrect is the all-items-match AND complete-assignment check.
  let correctCount = 0;
  items.forEach((item, idx) => {
    // Defensive: coerce both sides to number in case of string/number mismatch
    if (Number(userAssignments[String(idx)]) === Number(item.correctCategory)) {
      correctCount++;
    }
  });
  const isCorrect = Object.keys(userAssignments).length === items.length && correctCount === items.length;
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: `${correctCount}/${items.length} correct`
  };
};

// ── Spelling Pick (SPR grapheme-choice cloze) ────────────────

export const validateSpellingPick: ValidatorFn = (userAnswer, question) => {
  let picks: string[] = [];
  try {
    const parsed = JSON.parse(String(userAnswer));
    if (Array.isArray(parsed)) picks = parsed.map(p => String(p));
  } catch {

    /* empty */}
  const config = question.data?.spellingPick;
  const words = config?.words ?? [];
  let correctCount = 0;
  const isCorrect = words.length > 0 && picks.length === words.length && words.every((word, idx) => {
    const match = (picks[idx] ?? '').toLowerCase() === word.correct.toLowerCase();
    if (match) correctCount++;
    return match;
  });

  // Build a readable "wat het had moeten zijn"-string for feedback.
  const correctWords = words.map(w => `${w.before}${w.correct}${w.after}`).join(', ');
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: isCorrect ? `${correctCount}/${words.length} goed` : `${correctCount}/${words.length} goed (juist: ${correctWords})`
  };
};

// ── Verb Ending (SWW werkwoord-uitgang cloze) ────────────────

export const validateVerbEnding: ValidatorFn = (userAnswer, question) => {
  let picks: string[] = [];
  try {
    const parsed = JSON.parse(String(userAnswer));
    if (Array.isArray(parsed)) picks = parsed.map(p => String(p));
  } catch {

    /* empty */}
  const config = question.data?.verbEnding;
  const items = config?.items ?? [];
  let correctCount = 0;
  const isCorrect = items.length > 0 && picks.length === items.length && items.every((item, idx) => {
    const match = (picks[idx] ?? '').toLowerCase() === item.correct.toLowerCase();
    if (match) correctCount++;
    return match;
  });

  // Build a readable "wat het had moeten zijn"-string for feedback.
  const correctWords = items.map(it => `${it.before ?? ''}${it.stem}${it.correct}${it.after ?? ''}`).join(', ');
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: isCorrect ? `${correctCount}/${items.length} goed` : `${correctCount}/${items.length} goed (juist: ${correctWords})`
  };
};

// ── Word Builder (SPR full-word producer / dictee) ───────────

export const validateWordBuilder: ValidatorFn = (userAnswer, question) => {
  let built: string[] = [];
  try {
    const parsed = JSON.parse(String(userAnswer));
    if (Array.isArray(parsed)) built = parsed.map(b => String(b));
  } catch {

    /* empty */}
  const config = question.data?.wordBuilder;
  const items = config?.items ?? [];
  let correctCount = 0;
  const isCorrect = items.length > 0 && built.length === items.length && items.every((item, idx) => {
    const match = (built[idx] ?? '').trim().toLowerCase() === item.word.toLowerCase();
    if (match) correctCount++;
    return match;
  });

  // Build a readable "wat het had moeten zijn"-string for feedback.
  const correctWords = items.map(it => it.word).join(', ');
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: isCorrect ? `${correctCount}/${items.length} goed` : `${correctCount}/${items.length} goed (juist: ${correctWords})`
  };
};

// ── Coordinate Plot ──────────────────────────────────────────

export const validateCoordinatePlot: ValidatorFn = (userAnswer, question) => {
  let pickedPoint: {
    x: number;
    y: number;
  } | null = null;
  try {
    pickedPoint = JSON.parse(String(userAnswer));
  } catch {

    /* empty */}
  const config = question.data?.coordinatePlot || question.data || {};
  const correctPoint = config.correctPoint;
  if (!pickedPoint || !correctPoint) {
    return {
      isCorrect: false,
      distractorIndex: 0
    };
  }
  const isCorrect = pickedPoint.x === correctPoint.x && pickedPoint.y === correctPoint.y;
  return {
    isCorrect,
    distractorIndex: isCorrect ? undefined : 0,
    displayAnswer: isCorrect ? `(${correctPoint.x}, ${correctPoint.y})` : `(${pickedPoint.x}, ${pickedPoint.y}) (juist: (${correctPoint.x}, ${correctPoint.y}))`,
    correctAnswerDisplay: `(${correctPoint.x}, ${correctPoint.y})`
  };
};